package org.teddapp.project.ui.today

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Task
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.InkButton
import org.teddapp.project.ui.InkGhostButton
import org.teddapp.project.ui.TeddInkBlock
import org.teddapp.project.ui.TeddType
import androidx.compose.ui.draw.rotate
import org.teddapp.project.core.CoreReaction
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.backup.BackupGapLevel

/**
 * Briques visuelles d'Aujourd'hui, partagées entre les mises en page compacte et étendue.
 * Aucune brique ne lit la base : tout passe par [TodayModel] ou des paramètres.
 */

/** Phrase d'accroche sous le salut, selon le nombre de tâches ouvertes. */
internal fun todayTagline(openTasks: Int): String =
    if (openTasks > 0) {
        "Tu as $openTasks tâche${if (openTasks > 1) "s" else ""} qui méritent ton attention."
    } else {
        "Rien d'urgent — profite pour avancer sur le profond."
    }

// ──────────────────────────────────────────────────────────────────
// Hero étendu : anneau 120 + salut + deux actions
// ──────────────────────────────────────────────────────────────────
// Compact : en-tête (logo, date, salut, réglages) puis bloc score
// ──────────────────────────────────────────────────────────────────
/**
 * Salut d'Aujourd'hui, en mise en page étendue : la date, le nom, une phrase, une capture.
 * Pas d'anneau ni de dégradé : la seule chose forte de l'écran est la recommandation.
 */
@Composable
internal fun TodayGreeting(openTasks: Int, onQuickCapture: () -> Unit, horizontalPadding: Dp) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(start = horizontalPadding, end = horizontalPadding, top = 30.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(20.dp),
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                TeddIcon(name = "home", size = 12.dp, color = TeddTokens.InkMuted)
                Text("${shortFriendlyToday()} · ${fmtTime()}", style = TeddType.Overline, color = TeddTokens.InkMuted)
            }
            Spacer(modifier = Modifier.height(6.dp))
            org.teddapp.project.ui.TeddFitText(
                text = greetingForUser(LocalAppSettings.current.userName),
                style = TeddType.DisplayLg,
                minFontSize = 24.sp,
                maxLines = 2,
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = todayTagline(openTasks),
                color = TeddTokens.InkMuted,
                style = TeddType.Body,
                modifier = Modifier.widthIn(max = 560.dp),
            )
        }
        TeddButton(
            text = "Capture rapide",
            onClick = onQuickCapture,
            kind = TeddButtonKind.Secondary,
            size = TeddButtonSize.Md,
            leadingIcon = { TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.Ink, strokeWidth = 2.4.dp) },
        )
    }
}

@Composable
internal fun TodayCompactHeader(onOpenSettings: () -> Unit, onQuickCapture: () -> Unit = {}) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .statusBarsPadding()
            .padding(start = 16.dp, end = 16.dp, top = 14.dp, bottom = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        TeddLogo(size = 28.dp)
        Column(modifier = Modifier.weight(1f)) {
            Text(
                shortFriendlyToday().uppercase(),
                color = TeddTokens.InkMuted,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.6.sp,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                greetingForUser(LocalAppSettings.current.userName),
                style = TextStyle(
                    color = TeddTokens.Ink,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = (-0.3).sp,
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(TeddTokens.SurfaceSunken, CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onQuickCapture,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "plus", size = 16.dp, color = TeddTokens.InkMuted, strokeWidth = 2.2.dp)
        }
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(TeddTokens.SurfaceSunken, CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onOpenSettings,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "settings", size = 16.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Capture rapide : une ligne, la tâche part dans le pool focus du jour
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun TodayQuickCapture(model: TodayModel, horizontalPadding: Dp) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = horizontalPadding, end = horizontalPadding, top = 10.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.2.dp)
        BasicTextField(
            value = model.quickDraft,
            onValueChange = { model.quickDraft = it },
            modifier = Modifier.weight(1f),
            singleLine = true,
            textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(onDone = { model.submitQuick() }),
            decorationBox = { inner ->
                if (model.quickDraft.isEmpty()) {
                    Text("Une chose à faire aujourd'hui…", color = TeddTokens.InkMuted, fontSize = 13.sp)
                }
                inner()
            },
        )
        TeddButton(
            text = "Ajouter",
            onClick = { model.submitQuick() },
            kind = TeddButtonKind.Primary,
            size = TeddButtonSize.Sm,
            enabled = model.quickDraft.trim().isNotEmpty(),
        )
        GhostLink("Fermer", onClick = { model.quickOpen = false })
    }
}

// ──────────────────────────────────────────────────────────────────
// Bandeau d'alerte sauvegarde
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun GapStrip(level: BackupGapLevel, message: String, horizontalPadding: Dp, onOpenBackup: () -> Unit) {
    val color = when (level) {
        BackupGapLevel.Danger -> TeddTokens.Danger
        BackupGapLevel.Warning -> TeddTokens.Warning
        else -> TeddTokens.LimeStrong
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                Brush.horizontalGradient(
                    colors = listOf(color.copy(alpha = 0.10f), Color.Transparent),
                ),
            )
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .padding(horizontal = horizontalPadding, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        TeddIcon(name = "bell", size = 14.dp, color = color, strokeWidth = 2.dp)
        Row(modifier = Modifier.weight(1f)) {
            Text(
                text = "Sync : ",
                color = color,
                fontSize = 12.5.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Text(
                text = message,
                color = TeddTokens.Ink,
                fontSize = 12.5.sp,
                fontWeight = FontWeight.Medium,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
        GhostLink("Voir Sync →", onOpenBackup)
    }
}

// ──────────────────────────────────────────────────────────────────
// Carte générique (en-tête + corps)
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun HomeCard(
    label: String,
    icon: String,
    accent: Color,
    count: String? = null,
    cta: (@Composable () -> Unit)? = null,
    body: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(24.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .background(accent.copy(alpha = 0.10f), RoundedCornerShape(TeddTokens.RXs)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = icon, size = 12.dp, color = accent, strokeWidth = 2.dp)
            }
            Text(label, color = TeddTokens.Ink, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
            if (count != null) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                        .padding(horizontal = 7.dp, vertical = 1.dp),
                ) {
                    Text(count, color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.Medium)
                }
            }
            Spacer(modifier = Modifier.weight(1f))
            cta?.invoke()
        }
        Column(modifier = Modifier.padding(14.dp)) {
            body()
        }
    }
}

@Composable
internal fun GhostLink(label: String, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RXs))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 6.dp, vertical = 3.dp),
    ) {
        Text(label, color = TeddTokens.LimeStrong, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
    }
}

@Composable
private fun EmptyMini(icon: String, text: String) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TeddIcon(name = icon, size = 18.dp, color = TeddTokens.InkSubtle)
        Text(text, color = TeddTokens.InkMuted, fontSize = 12.sp)
    }
}

// ──────────────────────────────────────────────────────────────────
// Tâches du jour
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun TasksCard(
    tasks: List<Task>,
    onToggle: (Task) -> Unit,
    onOpenPool: () -> Unit,
) {
    HomeCard(
        label = "À faire aujourd'hui",
        icon = "check",
        accent = TeddTokens.LimeStrong,
        count = tasks.size.toString(),
        cta = { GhostLink("Pool focus →", onOpenPool) },
    ) {
        if (tasks.isEmpty()) {
            EmptyMini("check", "Tout est validé pour aujourd'hui.")
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
                tasks.forEach { t -> HomeTaskRow(t, onToggle = { onToggle(t) }) }
            }
        }
    }
}

@Composable
private fun HomeTaskRow(task: Task, onToggle: () -> Unit) {
    val source = task.source()
    val sourceColor = source.color
    val done = task.status == "done"
    val dl = daysLeft(task.due_date)
    val dueColor = when {
        dl == null -> TeddTokens.InkMuted
        dl < 0 -> TeddTokens.Danger
        dl <= 2 -> TeddTokens.Warning
        else -> TeddTokens.InkMuted
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(18.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .then(
                    if (done) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                    else Modifier.border(1.5.dp, TeddTokens.InkSubtle, RoundedCornerShape(TeddTokens.RXs)),
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggle,
                ),
            contentAlignment = Alignment.Center,
        ) {
            if (done) TeddIcon(name = "check", size = 11.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = task.title,
                color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                textDecoration = if (done) TextDecoration.LineThrough else TextDecoration.None,
            )
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(5.dp),
            ) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(3.dp))
                        .background(sourceColor.copy(alpha = 0.10f), RoundedCornerShape(3.dp))
                        .padding(horizontal = 4.dp, vertical = 1.dp),
                ) {
                    Text(
                        source.label,
                        color = sourceColor,
                        fontSize = 8.5.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.3.sp,
                    )
                }
                task.estimated_minutes?.let { em ->
                    Text(
                        "$em min",
                        color = TeddTokens.InkMuted,
                        fontSize = 10.5.sp,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
        if (!task.due_date.isNullOrBlank()) {
            Text(
                text = fmtDueShort(task.due_date),
                color = dueColor,
                fontSize = 11.sp,
                fontWeight = FontWeight.Medium,
            )
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Argent du mois
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun MoneyCard(
    courantName: String,
    courantBalance: Long,
    courantColorHex: String,
    income: Long,
    expense: Long,
    balance30: List<Long>,
    hideBalance: Boolean,
    onToggleHide: () -> Unit,
    onOpenFinance: () -> Unit,
) {
    val courantColor = parseTodayHex(courantColorHex)
    val delta30 = if (balance30.size >= 2) balance30.last() - balance30.first() else 0L
    val deltaColor = if (delta30 >= 0) TeddTokens.Success else TeddTokens.Danger
    val deltaSign = if (delta30 >= 0) "+" else "−"
    val masked = "•••• F"
    HomeCard(
        label = "Argent — ${currentMonthName()}",
        icon = "wallet",
        accent = TeddTokens.LimeStrong,
        cta = {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = onToggleHide,
                        )
                        .padding(horizontal = 6.dp, vertical = 3.dp),
                ) {
                    TeddIcon(
                        name = if (hideBalance) "eye-off" else "eye",
                        size = 14.dp,
                        color = if (hideBalance) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                    )
                }
                GhostLink("Voir Finance →", onOpenFinance)
            }
        },
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(
                        Brush.linearGradient(
                            colors = listOf(courantColor.copy(alpha = 0.10f), courantColor.copy(alpha = 0.02f)),
                            start = Offset.Zero,
                            end = Offset.Infinite,
                        ),
                        RoundedCornerShape(TeddTokens.RSm),
                    )
                    .border(1.dp, courantColor.copy(alpha = 0.20f), RoundedCornerShape(TeddTokens.RSm))
                    .padding(14.dp),
            ) {
                Text(
                    text = courantName.uppercase(),
                    color = TeddTokens.InkMuted,
                    fontSize = 10.5.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 0.5.sp,
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = if (hideBalance) masked else formatXof(courantBalance),
                    style = TextStyle(
                        color = TeddTokens.Ink,
                        fontSize = 26.sp,
                        fontWeight = FontWeight.SemiBold,
                        letterSpacing = (-0.6).sp,
                    ),
                )
                if (balance30.isNotEmpty() && !hideBalance) {
                    Spacer(modifier = Modifier.height(6.dp))
                    BalanceSparkline(
                        values = balance30,
                        accent = courantColor,
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = "30 derniers jours",
                            color = TeddTokens.InkMuted,
                            fontSize = 10.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.4.sp,
                        )
                        Spacer(modifier = Modifier.weight(1f))
                        Text(
                            text = "$deltaSign${formatXofCompact(if (delta30 < 0) -delta30 else delta30)}",
                            color = deltaColor,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                MiniFinTile(
                    label = "Entrées",
                    value = if (hideBalance) masked else "+${formatXofCompact(income)}",
                    color = TeddTokens.Success,
                    modifier = Modifier.weight(1f),
                )
                MiniFinTile(
                    label = "Sorties",
                    value = if (hideBalance) masked else "−${formatXofCompact(expense)}",
                    color = TeddTokens.Danger,
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

@Composable
private fun MiniFinTile(label: String, value: String, color: Color, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 8.dp),
    ) {
        Text(
            text = label.uppercase(),
            color = TeddTokens.InkMuted,
            fontSize = 10.sp,
            fontWeight = FontWeight.SemiBold,
            letterSpacing = 0.4.sp,
        )
        Spacer(modifier = Modifier.height(1.dp))
        Text(text = value, color = color, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
    }
}

// ──────────────────────────────────────────────────────────────────
// Formats locaux
// ──────────────────────────────────────────────────────────────────
internal fun parseTodayHex(hex: String): Color {
    val clean = hex.trimStart('#')
    val rgb = clean.toLongOrNull(16) ?: 0x6366F1L
    return Color(0xFF000000L or rgb)
}

/** Nom du mois courant en minuscules ("septembre"). */
internal fun currentMonthName(iso: String = TODAY_HOME_ISO): String {
    val mIdx = (iso.substring(5, 7).toIntOrNull() ?: 1) - 1
    return MONTH_NAMES_FR.getOrNull(mIdx) ?: ""
}

private fun formatXof(amount: Long): String {
    val abs = if (amount < 0) -amount else amount
    val str = abs.toString().reversed().chunked(3).joinToString(" ").reversed()
    val sign = if (amount < 0) "−" else ""
    return "$sign$str F"
}

/** "845 250" → "845 k", "2 300 000" → "2,3 M". */
private fun formatXofCompact(amount: Long): String {
    val abs = if (amount < 0) -amount else amount
    return when {
        abs >= 1_000_000 -> {
            val whole = abs / 1_000_000
            val tenths = (abs % 1_000_000) / 100_000
            if (tenths == 0L) "$whole M" else "$whole,$tenths M"
        }
        abs >= 1_000 -> "${abs / 1_000} k"
        else -> "$abs F"
    }
}

// ──────────────────────────────────────────────────────────────────
// Recommandation : une seule chose, un seul bouton
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun TodayRecommendation(
    reco: Recommendation?,
    onStart: () -> Unit,
    onAlternate: () -> Unit,
    modifier: Modifier = Modifier,
    /** Phrase de l'agent du noyau, quand il est branché. */
    coreLabel: String? = null,
) {
    if (reco == null) {
        TeddInkBlock(
            modifier = modifier,
            eyebrow = "Rien d'urgent",
            title = "Rien ne t'attend aujourd'hui.",
            subtitle = "Profite pour avancer sur le profond, ou capture ce qui te vient.",
        )
        return
    }
    TeddInkBlock(
        modifier = modifier,
        eyebrow = coreLabel?.let { "Recommandé · $it" } ?: "Recommandé · ${reco.minutes} min · ${reco.context}",
        title = reco.task.title,
        subtitle = if (reco.task.due_date.isNullOrBlank()) null else "Échéance ${fmtDueShort(reco.task.due_date)}",
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 4.dp)) {
            InkButton(
                text = "Commencer",
                onClick = onStart,
                icon = { TeddIcon(name = "play", size = 13.dp, color = TeddTokens.Ink, strokeWidth = 2.4.dp) },
            )
            InkGhostButton(text = "Autre chose", onClick = onAlternate)
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Trois nombres : minutes de focus, traversées, décisions
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun TodayThreeNumbers(
    focusMinutes: Int,
    crossings: Int,
    rationalRate: Int?,
    cardsDue: Int = 0,
    onReview: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
            DayNumber("focus", if (focusMinutes > 0) "$focusMinutes min" else "—", Modifier.weight(1f))
            DayNumber("traversées", if (crossings > 0) crossings.toString() else "—", Modifier.weight(1f))
            DayNumber("décisions", rationalRate?.let { "$it %" } ?: "—", Modifier.weight(1f))
        }
        if (cardsDue > 0 && onReview != null) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = "$cardsDue carte${if (cardsDue > 1) "s" else ""} à réviser",
                    color = TeddTokens.InkMuted,
                    fontSize = 13.sp,
                )
                GhostLink("Réviser →", onClick = onReview)
            }
        }
    }
}

@Composable
private fun DayNumber(label: String, value: String, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        Text(text = value, style = TeddType.Numeral.copy(fontSize = 22.sp, lineHeight = 26.sp), color = TeddTokens.Ink)
        Text(text = label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
    }
}

// ──────────────────────────────────────────────────────────────────
// Fil des réactions du noyau
// ──────────────────────────────────────────────────────────────────
/**
 * Le fil du jour : une réaction du noyau par ligne, chronologique et fermable.
 * Une réaction lue disparaît. Rien ne s'accumule en badge, rien ne bloque l'écran.
 */
@Composable
internal fun TodayReactionFeed(
    reactions: List<CoreReaction>,
    onDismiss: (CoreReaction) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (reactions.isEmpty()) return
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text("FIL", style = TeddType.Overline, color = TeddTokens.InkMuted)
        reactions.forEach { r ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RLg))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RLg))
                    .padding(horizontal = 14.dp, vertical = 12.dp),
                verticalAlignment = Alignment.Top,
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(26.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RSm)),
                    contentAlignment = Alignment.Center,
                ) {
                    TeddIcon(name = reactionIcon(r.kind), size = 12.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.dp)
                }
                Column(modifier = Modifier.weight(1f)) {
                    Text(reactionTitle(r.kind), color = TeddTokens.Ink, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
                    Text(r.message, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 14.sp)
                }
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onDismiss(r) },
                        )
                        .padding(6.dp)
                        .rotate(45f),
                ) {
                    TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
                }
            }
        }
    }
}

/** Titre lisible d'une réaction, à partir de son genre technique. */
private fun reactionTitle(kind: String): String = when {
    kind.contains("Socratic", ignoreCase = true) -> "Tuteur socratique"
    kind.contains("Somatic", ignoreCase = true) -> "Pleine conscience"
    kind.contains("Coherence", ignoreCase = true) -> "Cohérence"
    kind.contains("Compassion", ignoreCase = true) || kind.contains("Growth", ignoreCase = true) -> "Sans pénalité"
    kind.contains("Explanation", ignoreCase = true) -> "Auto-explication"
    else -> "Noyau"
}

private fun reactionIcon(kind: String): String = when {
    kind.contains("Socratic", ignoreCase = true) -> "spark"
    kind.contains("Somatic", ignoreCase = true) -> "heart"
    kind.contains("Coherence", ignoreCase = true) -> "wallet"
    else -> "bell"
}
